import { useState, useEffect, useRef } from "react";
import { Box, Button } from "@mui/material";
import SbuxTranslate from "../../../../components/SbuxTranslate";
import SbuxGenericAutocomplete from "../../../../components/SbuxAutoComplete/SbuxGenericAutocomplete";
import ConfirmActionModal from "./ConfirmActionModal";

import {
  retrieveApiData,
  resetApiData,
  postApiData,
} from "../../../../services/connect";
import {
  getConnectDatasetForApi,
  getConnectStatusSelector,
  getConnectErrorMessageSelector,
} from "../../../../selectors/connectSelector";
import {
  getArnSelector,
  getConnectedInstanceSelector,
} from "../../../../selectors/userInstanceSelector";

import { sortingComparator } from "../../../../utils/arrayUtil";
import { validateForm } from "../../../../utils/formValidation";
import {
  SNACK_BARS_SEVERITY_ERROR,
  SNACK_BARS_SEVERITY_SUCCESS,
} from "../../../../constants";
import styles from "../styles";
import useCss from "../../../../hooks/useCss";
import { useAppDispatch, useAppSelector } from "../../../../store/types";
import type { ReactNode } from "react";
import type { ConnectedInstance } from "../../../../slices/instanceSlice";
import type {
  PhoneNumberRecord,
  InstanceRecord,
  ContactFlowRecord,
} from "./types";

interface AutocompleteOption {
  [key: string]: unknown;
}

interface DatasetState<T extends AutocompleteOption> {
  options: T[];
  loading: boolean;
  isOptionEqualToValue?: (option: T, value: T) => boolean;
  getOptionLabel?: (option: T) => string;
}

interface StatusReport {
  message: ReactNode;
  severity: string;
}

interface Props {
  onProcessStatusChange?: (report: StatusReport) => void;
  languages?: unknown;
}

const API_NAME = {
  PHONE_NUMBER_LIST: "phone-number-list",
  INSTANCE_LIST: "instance-list",
  CONTACT_FLOW_LIST: "contact-flow-list",
  PORT_PHONE: "port-Phone",
};

const STATUS_REPORT: Record<string, StatusReport> = {
  ["post:pending"]: {
    message: <SbuxTranslate>{`Operation in progress`}</SbuxTranslate>,
    severity: SNACK_BARS_SEVERITY_SUCCESS,
  },
  ["post:success"]: {
    message: (
      <SbuxTranslate>{`Phone Number ported successfully`}</SbuxTranslate>
    ),
    severity: SNACK_BARS_SEVERITY_SUCCESS,
  },
  ["post:rejected"]: {
    message: (
      <SbuxTranslate>{`An error has ocurred while porting a Phone Number`}</SbuxTranslate>
    ),
    severity: SNACK_BARS_SEVERITY_ERROR,
  },
  ["get:rejected"]: {
    message: (
      <SbuxTranslate>{`An error ocurred while loading data`}</SbuxTranslate>
    ),
    severity: SNACK_BARS_SEVERITY_ERROR,
  },
};

/**
 * PortPhoneNumber component
 */
const PortPhoneNumber = ({ onProcessStatusChange }: Props = {}) => {
  const classes = useCss(styles);
  const dispatch = useAppDispatch();
  const emptyDataset: DatasetState<AutocompleteOption> = {
    options: [],
    loading: false,
  };

  const [phoneNumberDataset, setPhoneNumberDataset] = useState<
    DatasetState<PhoneNumberRecord>
  >({
    options: [],
    loading: false,
  });
  const [destinationInstanceDataset, setDestinationInstanceDataset] = useState<
    DatasetState<InstanceRecord>
  >({
    options: [],
    loading: false,
  });
  const [contactFlowDataset, setContactFlowDataset] = useState<
    DatasetState<ContactFlowRecord>
  >({
    options: [],
    loading: false,
  });

  const [phoneNumber, setPhoneNumber] = useState<PhoneNumberRecord | null>(
    null,
  );
  const [instance, setInstance] = useState<InstanceRecord | null>(null);
  const [contactFlow, setContactFlow] = useState<ContactFlowRecord | null>(
    null,
  );
  const [confirmAction, setConfirmAction] = useState(false);
  const [formValidity, setFormValidity] = useState<Record<string, string>>({});
  const [formSubmitted, setFormSubmitted] = useState(false);

  const instanceArn = useAppSelector(getArnSelector);
  const connectedInstance = useAppSelector(
    getConnectedInstanceSelector,
  ) as ConnectedInstance | null;
  const phoneNumberList = useAppSelector(
    getConnectDatasetForApi(API_NAME.PHONE_NUMBER_LIST),
  ) as PhoneNumberRecord[] | null;
  const instanceList = useAppSelector(
    getConnectDatasetForApi(API_NAME.INSTANCE_LIST),
  ) as InstanceRecord[] | null;
  const contactFlowList = useAppSelector(
    getConnectDatasetForApi(API_NAME.CONTACT_FLOW_LIST),
  ) as ContactFlowRecord[] | null;
  const processStatus = useAppSelector(getConnectStatusSelector) as string;
  const connectErrorMessage = useAppSelector(getConnectErrorMessageSelector);

  const lastProcessStatus = useRef<string | null>(null);
  const form = useRef<HTMLFormElement | null>(null);

  const chainOfAction: Record<string, () => void> = {
    ["post:success"]: () => {
      resetForm(true);
    },
    ["get:rejected"]: () => {
      resetLoadingState();
    },
  };

  const retrieveData = async (queryParams: Record<string, string>) => {
    const payload = {
      instanceId: connectedInstance?.instanceId,
      queryParams,
    };

    await dispatch(retrieveApiData(payload));
  };

  const postData = async (
    queryParams: Record<string, string>,
    body: Record<string, string | undefined>,
  ) => {
    const payload = {
      instanceId: connectedInstance?.instanceId,
      id: "port-phone-number",
      queryParams,
      body,
    };
    await dispatch(postApiData(payload));
  };

  const resetForm = (onSuccess = false) => {
    setFormSubmitted(false);
    setPhoneNumber(null);
    setInstance(null);
    setContactFlow(null);
    if (onSuccess) {
      setPhoneNumberDataset({ options: [], loading: false });
    }
    setContactFlowDataset({ options: [], loading: false });
    setFormValidity({});
  };

  const resetLoadingState = () => {
    if (phoneNumberDataset.loading) {
      setPhoneNumberDataset((prev) => ({ ...prev, loading: false }));
    }
    if (destinationInstanceDataset.loading) {
      setDestinationInstanceDataset((prev) => ({ ...prev, loading: false }));
    }
    if (contactFlowDataset.loading) {
      setContactFlowDataset((prev) => ({ ...prev, loading: false }));
    }
  };

  useEffect(() => {
    return () => {
      dispatch(
        resetApiData({
          datasets: [
            API_NAME.PHONE_NUMBER_LIST,
            API_NAME.INSTANCE_LIST,
            API_NAME.CONTACT_FLOW_LIST,
          ],
        }),
      );
    };
  }, []);

  useEffect(() => {
    if (lastProcessStatus.current !== processStatus) {
      lastProcessStatus.current = processStatus;
      if (typeof onProcessStatusChange === "function") {
        const baseReport = STATUS_REPORT[processStatus];
        if (baseReport) {
          const report = { ...baseReport };
          if (processStatus === "post:rejected") {
            report.message = connectErrorMessage as ReactNode;
          }
          onProcessStatusChange(report);
        }
      }

      const followingAction = chainOfAction[processStatus];
      if (typeof followingAction === "function") {
        followingAction();
      }
    }
  }, [processStatus, connectErrorMessage]);

  useEffect(() => {
    if (phoneNumberList?.length) {
      setPhoneNumberDataset({
        loading: false,
        options: phoneNumberList
          .slice()
          .sort(sortingComparator("asc", "PhoneNumber")),
        isOptionEqualToValue: (option, value) =>
          option.PhoneNumberId === value.PhoneNumberId,
        getOptionLabel: (option) => option.PhoneNumber,
      });
    }
  }, [phoneNumberList]);

  useEffect(() => {
    if (instanceList?.length) {
      setDestinationInstanceDataset({
        loading: false,
        options: instanceList
          .slice()
          .filter((record) => record.Id !== connectedInstance?.instanceId)
          .sort(sortingComparator("asc", "InstanceAlias")),
        isOptionEqualToValue: (option, value) => option.Id === value.Id,
        getOptionLabel: (option) => option.InstanceAlias,
      });
    }
  }, [instanceList]);

  useEffect(() => {
    if (contactFlowList?.length) {
      setContactFlowDataset({
        loading: false,
        options: contactFlowList.slice().sort(sortingComparator("asc", "Name")),
        isOptionEqualToValue: (option, value) => option?.Id === value.Id,
        getOptionLabel: (option) => option?.Name,
      });
    }
  }, [contactFlowList]);

  useEffect(() => {
    if (formSubmitted) {
      updateFormValidity();
    }
  }, [phoneNumber, instance]);

  const updateFormValidity = () => {
    const validation = validateForm(form.current as HTMLFormElement, [
      "phoneNumber",
      "instance",
    ]);
    setFormValidity(validation as Record<string, string>);
    return validation;
  };

  const handleOpenPhoneNumberList = () => {
    if (!phoneNumberDataset.options.length) {
      const queryParams = {
        apiName: API_NAME.PHONE_NUMBER_LIST,
        instanceArn: encodeURIComponent(instanceArn as string),
      };
      retrieveData(queryParams);
      setPhoneNumberDataset((prev) => ({ ...prev, loading: true }));
    }
  };

  const handleOpenInstanceList = () => {
    if (!destinationInstanceDataset.options.length) {
      const queryParams = {
        apiName: API_NAME.INSTANCE_LIST,
      };
      retrieveData(queryParams);
      setDestinationInstanceDataset((prev) => ({ ...prev, loading: true }));
    }
  };

  const handleOpenContactFlowList = () => {
    if (!contactFlowDataset.options.length && instance) {
      const queryParams = {
        apiName: API_NAME.CONTACT_FLOW_LIST,
        instanceArn: encodeURIComponent(instance.Arn),
      };
      retrieveData(queryParams);
      setContactFlowDataset((prev) => ({ ...prev, loading: true }));
    }
  };

  const handleOnChangePhoneNumber = (
    _event: unknown,
    newValue: PhoneNumberRecord | null,
  ) => {
    setPhoneNumber(newValue);
  };

  const handleOnChangeDestinationInstance = (
    _event: unknown,
    newValue: InstanceRecord | null,
  ) => {
    setInstance(newValue);
    setContactFlowDataset((prev) => ({ ...prev, options: [], loading: false }));
    setContactFlow(null);
  };

  const handleClearForm = () => {
    resetForm();
  };

  const handleOnClickPortPhone = () => {
    setFormSubmitted(true);
    const validation = updateFormValidity();

    if (!(validation as { invalid?: boolean }).invalid) {
      setConfirmAction(true);
    }
  };

  const handleOnCloseModal = () => {
    setConfirmAction(false);
  };

  const handleConfirmDialog = async () => {
    setConfirmAction(false);
    if (!phoneNumber || !instance) return;

    const queryParams = {
      apiName: API_NAME.PORT_PHONE,
    };
    const body = {
      phoneNumberId: phoneNumber.PhoneNumberId,
      instanceArn: instance.Arn,
      contactFlowId: contactFlow?.Id,
    };
    await postData(queryParams, body);
  };

  return (
    <Box className={classes.formContainer}>
      <Box
        component="form"
        className={classes.formControl}
        noValidate
        autoComplete="off"
        ref={form}
      >
        <Box>
          <SbuxGenericAutocomplete
            className="formControl"
            id="phoneNumber"
            required
            onOpen={handleOpenPhoneNumberList}
            isOptionEqualToValue={phoneNumberDataset.isOptionEqualToValue}
            getOptionLabel={phoneNumberDataset.getOptionLabel}
            loading={phoneNumberDataset.loading}
            options={phoneNumberDataset.options}
            onChange={handleOnChangePhoneNumber}
            slotProps={{
              input: {
                label: <SbuxTranslate> {`Phone Number`}</SbuxTranslate>,
                required: true,
                helperText: formSubmitted ? formValidity["phoneNumber"] : null,
              },
            }}
            value={phoneNumber}
          />
        </Box>

        <Box>
          <SbuxGenericAutocomplete
            className="formControl"
            id="instance"
            onOpen={handleOpenInstanceList}
            isOptionEqualToValue={
              destinationInstanceDataset.isOptionEqualToValue
            }
            getOptionLabel={destinationInstanceDataset.getOptionLabel}
            loading={destinationInstanceDataset.loading}
            options={destinationInstanceDataset.options}
            onChange={handleOnChangeDestinationInstance}
            slotProps={{
              input: {
                label: <SbuxTranslate> {`Destination Instance`}</SbuxTranslate>,
                required: true,
                helperText: formSubmitted ? formValidity["instance"] : null,
              },
            }}
            value={instance}
          />
        </Box>

        <Box>
          <SbuxGenericAutocomplete
            className="formControl"
            id="contactFlow"
            onOpen={handleOpenContactFlowList}
            isOptionEqualToValue={contactFlowDataset.isOptionEqualToValue}
            getOptionLabel={contactFlowDataset.getOptionLabel}
            loading={contactFlowDataset.loading}
            options={contactFlowDataset.options}
            onChange={(_event: unknown, newValue: ContactFlowRecord | null) => {
              setContactFlow(newValue);
            }}
            slotProps={{
              input: {
                label: <SbuxTranslate> {`Contact Flow`}</SbuxTranslate>,
              },
            }}
            value={contactFlow}
            noOptionsText={"Must select a Destination Instance first"}
          />
        </Box>
      </Box>

      <Box className={classes.actionContainer}>
        <Button
          type="submit"
          variant="contained"
          size="medium"
          onClick={handleOnClickPortPhone}
        >
          <SbuxTranslate> {`Port Number`}</SbuxTranslate>
        </Button>
        <Button variant="outlined" size="medium" onClick={handleClearForm}>
          <SbuxTranslate> {`Clear`}</SbuxTranslate>
        </Button>
      </Box>

      {confirmAction && connectedInstance && phoneNumber && instance && (
        <ConfirmActionModal
          open={confirmAction}
          selections={{
            currentInstance: connectedInstance as Record<string, unknown>,
            phoneNumber,
            instance,
            contactFlow,
          }}
          handleOnClose={handleOnCloseModal}
          handleCancelDialog={handleOnCloseModal}
          handleConfirmDialog={handleConfirmDialog}
        />
      )}
    </Box>
  );
};

export default PortPhoneNumber;
